/**
 * Apply per-avatar speech settings from resolved host config onto the
 * browser STT engine (Aug 29 2026).
 *
 * This is host config — AvatarSettings.speech and/or a sibling
 * `speechPhrases` field — not an Avatar Loop frozen-contract change.
 * Decision Memory / SoW terms arrive later via `avatar:live:contextUpdate`
 * as additive `speechPhrases` (or `speech.phrases`) on the event detail;
 * this module never calls `/sow`.
 */

import {
  configureSttEngine,
  getSttEngineConfig,
  normalizeSpeechPhrases,
  setContextSpeechPhrases,
  type SttEngineConfig
} from './sttEngine';

export interface ApplyAvatarSpeechConfigOptions {
  /** Runtime API base (often `…/runtime-api`) used to mint Camlin tokens. */
  runtimeUrl?: string | null;
}

const DEFAULT_CAMLIN_RECOGNIZE_URL = 'https://speech.camlinconnect.com.au/api/v1/asr/recognize';
const DEFAULT_CAMLIN_STREAM_URL = 'https://speech.camlinconnect.com.au';

let contextListenerInstalled = false;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function trimSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

export function resolveCamlinTokenUrl(runtimeUrl?: string | null): string {
  const base = typeof runtimeUrl === 'string' ? trimSlash(runtimeUrl.trim()) : '';
  if (base) return `${base}/api/asr-token`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/runtime-api/api/asr-token`;
  }
  return '/runtime-api/api/asr-token';
}

/**
 * Pull phrases from a config-like object. Prefers nested `speech.phrases`
 * (builder host config) and also accepts top-level `speechPhrases` if the
 * sibling SDK slice lands that name.
 */
export function extractSpeechPhrasesFromConfig(input: unknown): string[] {
  const rec = asRecord(input);
  if (!rec) return [];
  const speech = asRecord(rec.speech);
  const nested = speech?.phrases ?? speech?.speechPhrases;
  const top = rec.speechPhrases ?? rec.phrases ?? rec.nluTerms;
  return normalizeSpeechPhrases([
    ...(Array.isArray(nested) ? nested : []),
    ...(Array.isArray(top) ? top : [])
  ]);
}

/**
 * Host context-update detail: additive fields beside the frozen
 * `contextSheet`. We never read `/sow`; hosts pass terms here.
 */
export function extractSpeechPhrasesFromContextDetail(detail: unknown): string[] {
  const rec = asRecord(detail);
  if (!rec) return [];
  return extractSpeechPhrasesFromConfig(rec);
}

function readSpeechSection(input: unknown): Record<string, unknown> | null {
  const rec = asRecord(input);
  if (!rec) return null;
  return asRecord(rec.speech);
}

export function installSpeechContextListener(): void {
  if (contextListenerInstalled) return;
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  contextListenerInstalled = true;
  window.addEventListener('avatar:live:contextUpdate', (event) => {
    const detail = (event as CustomEvent).detail;
    const phrases = extractSpeechPhrasesFromContextDetail(detail);
    const include = getSttEngineConfig().includeContextTerms;
    if (include === false) return;
    if (phrases.length > 0) setContextSpeechPhrases(phrases);
  });
}

/**
 * Apply a resolved avatar runtime config's speech block. Missing/empty
 * speech does not reset a host that already called `configureSttEngine`
 * (Companion sites). Explicit `engine` or phrases do.
 */
export function applyAvatarSpeechConfig(
  input: unknown,
  options: ApplyAvatarSpeechConfigOptions = {}
): void {
  installSpeechContextListener();

  const speech = readSpeechSection(input);
  const phrases = extractSpeechPhrasesFromConfig(input);

  if (!speech && phrases.length === 0) return;

  const existing = getSttEngineConfig();
  const engineRaw = speech && typeof speech.engine === 'string' ? speech.engine : undefined;
  const engine: SttEngineConfig['engine'] =
    engineRaw === 'camlin' || engineRaw === 'azure' ? engineRaw : existing.engine;

  const includeContextTerms = speech && typeof speech.includeContextTerms === 'boolean'
    ? speech.includeContextTerms
    : existing.includeContextTerms;
  const language = speech && typeof speech.language === 'string' && speech.language.trim()
    ? speech.language.trim()
    : existing.language;
  const streaming = speech && typeof speech.streaming === 'boolean'
    ? speech.streaming
    : existing.streaming;

  if (engine === 'camlin') {
    const tokenUrl =
      (speech && typeof speech.tokenUrl === 'string' && speech.tokenUrl.trim())
      || (existing.engine === 'camlin' ? existing.tokenUrl : undefined)
      || resolveCamlinTokenUrl(options.runtimeUrl);
    const recognizeUrl =
      (speech && typeof speech.recognizeUrl === 'string' && speech.recognizeUrl.trim())
      || existing.recognizeUrl
      || DEFAULT_CAMLIN_RECOGNIZE_URL;
    const streamUrl =
      (speech && typeof speech.streamUrl === 'string' && speech.streamUrl.trim())
      || existing.streamUrl
      || DEFAULT_CAMLIN_STREAM_URL;
    configureSttEngine({
      engine: 'camlin',
      tokenUrl,
      recognizeUrl,
      streamUrl,
      language,
      streaming,
      phrases,
      includeContextTerms
    });
  } else {
    configureSttEngine({
      engine: 'azure',
      phrases,
      includeContextTerms,
      language
    });
  }

  if (phrases.length > 0) {
    console.log('[speechRuntimeConfig] STT phrases applied', {
      engine,
      phraseCount: phrases.length,
      phrases
    });
  }
}
